/**
 * Map filter model + pure filtering helpers.
 *
 * Kept out of the drawer component so the map home can evaluate filters without
 * pulling the (lazily loaded) filter panel UI into the first bundle.
 */

import type { Purpose, PropertyType } from "@/lib/mock-data";
import type { ParkingType, VehicleType } from "@/lib/parking-mock-data";
import type { MapItem } from "@/lib/map-search";

export interface MapFilters {
  purpose: Purpose | "all";
  propertyType: PropertyType | "all";
  minPrice: number;
  maxPrice: number;
  distanceKm: number;
  parkingType: ParkingType | "all";
  vehicleType: VehicleType | "all";
  amenities: string[];
}

export const MAX_DISTANCE_KM = 25;

export const DEFAULT_MAP_FILTERS: MapFilters = {
  purpose: "all",
  propertyType: "all",
  minPrice: 0,
  maxPrice: 0,
  distanceKm: MAX_DISTANCE_KM,
  parkingType: "all",
  vehicleType: "all",
  amenities: [],
};

export const PROPERTY_AMENITIES = [
  "Parking",
  "Lift",
  "Power Backup",
  "Security",
  "Gated Community",
  "Swimming Pool",
  "Gym",
  "Garden",
  "Vaastu",
  "Furnished",
];

export const PARKING_AMENITIES = ["EV Charging", "CCTV", "Security", "Lighting", "24-hour access"];

export function countActiveFilters(f: MapFilters) {
  let n = 0;
  if (f.purpose !== "all") n += 1;
  if (f.propertyType !== "all") n += 1;
  if (f.minPrice > 0 || f.maxPrice > 0) n += 1;
  if (f.distanceKm < MAX_DISTANCE_KM) n += 1;
  if (f.parkingType !== "all") n += 1;
  if (f.vehicleType !== "all") n += 1;
  n += f.amenities.length;
  return n;
}

/** Apply every filter to the unified map feed. */
export function applyMapFilters(items: MapItem[], f: MapFilters): MapItem[] {
  const parkingOnly = f.purpose === "parking";
  return items.filter((i) => {
    // Parking is an exclusive mode: parking-only in, everything else out.
    if (parkingOnly) {
      if (i.kind !== "parking") return false;
    } else if (f.purpose !== "all") {
      if (i.purpose !== f.purpose) return false;
    }

    if (!parkingOnly && f.propertyType !== "all") {
      if (i.kind === "parking" || i.propertyType !== f.propertyType) return false;
    }
    if (f.minPrice > 0 && i.price < f.minPrice) return false;
    if (f.maxPrice > 0 && i.price > f.maxPrice) return false;
    if (i.distanceKm > f.distanceKm) return false;

    if (parkingOnly) {
      if (f.parkingType !== "all" && i.parkingType !== f.parkingType) return false;
      if (f.vehicleType !== "all" && !(i.vehicleTypes ?? []).includes(f.vehicleType)) return false;
    }

    if (f.amenities.length) {
      const have = i.amenities.map((a) => a.toLowerCase());
      if (!f.amenities.every((a) => have.some((h) => h.includes(a.toLowerCase())))) return false;
    }
    return true;
  });
}
